import React, { Fragment, useState, useEffect, useMemo } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import SubmitButton from "../Forms/SubmitButton";
import { getBooking } from "../../store/booking/bookingActions";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ModalAssign(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { tags } = useSelector((state) => state.tags);
  const { current_booking } = useSelector((state) => state.booking);
  const [allFoldersRaw, setAllFoldersRaw] = useState([]);
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState({
    noTags: [],
    hasTags: [],
    underProperty: [],
  });
  const [allowFolders, setAllowFolders] = useState(true);
  const [currentLocation, setCurrentLocation] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [processedTag, setProcessedTag] = useState("");
  const [selectedLinkedChats, setSelectedLinkedChats] = useState([]);
  const [linkedChats, setLinkedChats] = useState([]);
  const [isLoadingLinkedChats, setIsLoadingLinkedChats] = useState(true);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  const [ackBackward, setAckBackward] = useState(false);

  const isModalLoading = isLoadingLinkedChats || isLoadingFolders;

  // Compute filtered linked chats count (excluding funders, parents, sponsors)
  const displayableLinkedChatsCount = useMemo(() => {
    const excludedTenantTypes = ["FN", "PA", "SP"];
    const excludedLinkTypes = ["Funder", "Parent", "Sponsor"];
    return linkedChats.filter((chat) => {
      const displayBooking =
        chat.booking?.id === props.id ? chat.linked_booking : chat.booking;
      const linkType =
        chat.booking?.id === props.id ? chat.linked_type : chat.type;
      return (
        !excludedTenantTypes.includes(displayBooking?.tenant_type) &&
        !excludedLinkTypes.includes(linkType)
      );
    }).length;
  }, [linkedChats, props.id]);

  // Check if a property ID belongs to a grouped property (e.g. Unison 3,6,7)
  const propertyMatchesGroup = (bookingPropertyId, property) => {
    if (!bookingPropertyId || !property) return false;
    const groupedIds = property.grouped_ids || [property.id];
    return groupedIds.includes(Number(bookingPropertyId));
  };

  // Filter under_property folders based on selected property
  const getFilteredUnderPropertyFolders = (propertyId) => {
    if (!propertyId) return [];

    const excludedFolders = ["All", "Declined", "Cancelled"];

    // Find the selected property to get grouped_ids
    const selectedProp = properties.find((p) =>
      propertyMatchesGroup(propertyId, p),
    );
    const groupedIds = selectedProp?.grouped_ids || [Number(propertyId)];

    return allFoldersRaw
      .filter((folder) => {
        if (folder.is_property_container) return false;
        if (excludedFolders.includes(folder.name)) return false;
        if (!folder.under_property) return false;

        // If folder has no property_ids restriction, it applies to ALL properties
        if (
          !folder.property_ids ||
          folder.property_ids.length === 0
        ) {
          return true;
        }

        // Check if any of the grouped property IDs match the folder's property_ids
        return folder.property_ids.some((pid) => groupedIds.includes(pid));
      })
      .map((folder) => ({
        id: folder.id,
        name: folder.name === "All Residents" ? "Residents" : folder.name,
        display_name: folder.display_name || null,
        sort_order: folder.sort_order || 0,
        property_sort_order: folder.property_sort_order || 0,
      }))
      .sort((a, b) => a.property_sort_order - b.property_sort_order);
  };

  // Fetch linked chats when modal opens
  useEffect(() => {
    const fetchLinkedChats = async () => {
      if (!props.id) return;

      try {
        setIsLoadingLinkedChats(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}linked_chats/${props.id}`,
        );
        const chats = response.data || [];
        setLinkedChats(chats);
        setSelectedLinkedChats(
          chats
            .filter((chat) => {
              const displayBooking =
                chat.booking?.id === props.id
                  ? chat.linked_booking
                  : chat.booking;
              const linkType =
                chat.booking?.id === props.id ? chat.linked_type : chat.type;
              const excludedTenantTypes = ["FN", "PA", "SP"];
              const excludedLinkTypes = ["Funder", "Parent", "Sponsor"];
              return (
                !excludedTenantTypes.includes(displayBooking?.tenant_type) &&
                !excludedLinkTypes.includes(linkType)
              );
            })
            .map((chat) =>
              String(
                chat.booking?.id === props.id
                  ? chat.linked_booking_id
                  : chat.booking?.id,
              ),
            )
            .filter(Boolean),
        );
      } catch (error) {
        console.error("Failed to fetch linked chats:", error);
        setLinkedChats([]);
        setSelectedLinkedChats([]);
      } finally {
        setIsLoadingLinkedChats(false);
      }
    };

    fetchLinkedChats();
  }, [props.id]);

  // Filter tags based on selected location
  const getTagsForLocation = (locationName) => {
    return tags.filter((tag) => tag.parent_folder === locationName);
  };

  // Helper to determine tag status (notag, orphaned, validtag)
  const getTagDisplayStatus = (currentTagId, locationName) => {
    if (!currentTagId || currentTagId === 0 || currentTagId === "0") {
      return "notag";
    }

    const availableTags = getTagsForLocation(locationName).map((tag) =>
      Number(tag.id),
    );
    if (availableTags.includes(Number(currentTagId))) {
      return "validtag";
    }

    return "orphaned";
  };

  // Process the current tag when component mounts
  useEffect(() => {
    if (allowFolders) {
      getFolders();
    }

    if (current_booking?.location) {
      const tagStatus = getTagDisplayStatus(
        current_booking?.tag,
        current_booking?.location,
      );

      if (tagStatus === "orphaned") {
        setProcessedTag("orphaned");
      } else if (tagStatus === "notag") {
        setProcessedTag("0");
      } else {
        setProcessedTag(current_booking?.tag || "0");
      }
    }
  }, [allowFolders, current_booking]);

  async function getFolders() {
    setAllowFolders(false);
    setIsLoadingFolders(true);

    try {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_API_URL}folders/assign-modal`,
      });

      const fetchedFolders = response.data.folders || [];
      const fetchedProperties = response.data.properties || [];

      setAllFoldersRaw(fetchedFolders);
      setProperties(fetchedProperties);

      // Determine the booking's current property
      const bookingPropertyId = current_booking?.property_id;
      if (bookingPropertyId) {
        // Find which grouped property this belongs to
        const matchedProp = fetchedProperties.find((p) =>
          propertyMatchesGroup(bookingPropertyId, p),
        );
        if (matchedProp) {
          setSelectedPropertyId(String(matchedProp.id));
        } else {
          setSelectedPropertyId(String(bookingPropertyId));
        }
      }

      let noTagsFolders = [];
      let hasTagsFolders = [];

      const excludedFolders = ["All", "Declined", "Cancelled"];

      fetchedFolders.forEach((folder) => {
        if (folder.is_property_container) return;
        if (excludedFolders.includes(folder.name)) return;
        if (folder.under_property) return; // handled dynamically

        const folderObj = {
          id: folder.id,
          name: folder.name === "All Residents" ? "Residents" : folder.name,
          display_name: folder.display_name || null,
          sort_order: folder.sort_order || 0,
          property_sort_order: folder.property_sort_order || 0,
        };

        if (Number(folder.has_tags) === 1) {
          hasTagsFolders.push(folderObj);
        } else {
          noTagsFolders.push(folderObj);
        }

        if (
          folder.name === current_booking?.location ||
          (folder.name === "All Residents" &&
            current_booking?.location === "Residents")
        ) {
          setCurrentLocation(folderObj.name);
        }
      });

      noTagsFolders.sort((a, b) => a.sort_order - b.sort_order);
      hasTagsFolders.sort((a, b) => a.sort_order - b.sort_order);

      // Check if current location is an under_property folder
      if (!noTagsFolders.some((f) => f.name === current_booking?.location) &&
          !hasTagsFolders.some((f) => f.name === current_booking?.location)) {
        const underPropMatch = fetchedFolders.find(
          (f) =>
            f.under_property &&
            (f.name === current_booking?.location ||
              (f.name === "All Residents" &&
                current_booking?.location === "Residents")),
        );
        if (underPropMatch) {
          setCurrentLocation(
            underPropMatch.name === "All Residents"
              ? "Residents"
              : underPropMatch.name,
          );
        }
      }

      setLocations({
        noTags: noTagsFolders,
        hasTags: hasTagsFolders,
        underProperty: [], // populated dynamically via getFilteredUnderPropertyFolders
      });
    } catch (error) {
      console.error("Error fetching folders:", error);
    } finally {
      setIsLoadingFolders(false);
    }
  }

  // Handle linked chat selection
  const handleLinkedChatToggle = (chatId) => {
    const chatIdStr = String(chatId);
    setSelectedLinkedChats((prev) =>
      prev.map(String).includes(chatIdStr)
        ? prev.filter((id) => String(id) !== chatIdStr)
        : [...prev, chatIdStr],
    );
  };

  // Get the actual property_id to send to backend (resolve grouped IDs)
  const getSubmitPropertyId = () => {
    if (!selectedPropertyId) return null;
    const selectedProp = properties.find(
      (p) => String(p.id) === String(selectedPropertyId),
    );
    // For grouped properties like Unison, use the main ID (6)
    return selectedProp ? selectedProp.id : Number(selectedPropertyId);
  };

  // Moving a Residents chat to an earlier stage of the property pipeline (e.g.
  // back to Leads) is going backwards. We compare only within the property
  // grouping (under_property folders, ordered by property_sort_order), so
  // folders outside that grouping (Vacating, Archived, etc.) never trigger it,
  // and moving forward (Renewals, Collections, ...) is left alone.
  const isBackwardMove = (targetLocation) => {
    if (current_booking?.location !== "Residents") return false;
    if (!targetLocation || targetLocation === "Residents") return false;

    const normName = (n) => (n === "All Residents" ? "Residents" : n);
    const residents = allFoldersRaw.find(
      (f) => normName(f.name) === "Residents"
    );
    const target = allFoldersRaw.find(
      (f) => normName(f.name) === targetLocation
    );

    // Only warn for moves to another folder inside the same property pipeline.
    if (!residents || !target || !target.under_property) return false;

    // Backward = an earlier stage in the pipeline (lower property_sort_order).
    return (
      Number(target.property_sort_order) <
      Number(residents.property_sort_order)
    );
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    let finalTagValue = values.tag;
    if (values.tag === "orphaned") {
      finalTagValue = 0;
    }

    let submitData = {
      location: values.location,
      tag: finalTagValue,
      currentLocation: props.currentLocation,
      type: "folders",
      linkedChats: selectedLinkedChats,
    };

    // Include property_id if it changed
    const submitPropertyId = getSubmitPropertyId();
    if (submitPropertyId && submitPropertyId !== current_booking?.property_id) {
      submitData.property_id = submitPropertyId;
    }

    console.log("Submitting:", submitData);

    try {
      await axios({
        method: "put",
        url: `${process.env.REACT_APP_API_URL}assign/${props.id}`,
        data: submitData,
      });

      const title = "Booking Moved";
      const message = "Booking moved to " + values.location;
      toast(<NotificationToast title={title} message={message} />);

      setSubmitting(false);
      resetForm();
      dispatch(getBooking());
      dispatch(closeModal());
    } catch (error) {
      console.error("Error moving booking:", error);
      setSubmitting(false);
    }
  };

  // Dynamically compute under-property folders for the selected property
  const filteredUnderPropertyFolders = useMemo(() => {
    return getFilteredUnderPropertyFolders(selectedPropertyId);
  }, [selectedPropertyId, allFoldersRaw, properties]);

  // Find current property name for display
  const currentPropertyName = useMemo(() => {
    if (!current_booking?.property_id) return "No Property";
    const prop = properties.find((p) =>
      propertyMatchesGroup(current_booking.property_id, p),
    );
    return prop?.name || "Unknown";
  }, [current_booking?.property_id, properties]);

  // Check if form has changes (for dirty detection with property change)
  const hasPropertyChanged = useMemo(() => {
    const hadProperty = !!current_booking?.property_id;
    const hasSelected = !!selectedPropertyId;

    // Property added where there was none
    if (!hadProperty && hasSelected) return true;
    // Property removed where there was one
    if (hadProperty && !hasSelected) return true;
    // Both empty - no change
    if (!hadProperty && !hasSelected) return false;

    // Both set - check if different
    const selectedProp = properties.find(
      (p) => String(p.id) === String(selectedPropertyId),
    );
    if (!selectedProp) return false;
    return !propertyMatchesGroup(current_booking.property_id, selectedProp);
  }, [selectedPropertyId, current_booking?.property_id, properties]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 relative">
                {/* Loading Overlay */}
                {isModalLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50 rounded-lg">
                    <div className="text-center">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="h-12 w-12 text-primary animate-spin mb-4"
                        aria-hidden="true"
                      />
                      <p className="text-gray-600 font-medium">
                        Loading Move Booking data...
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        {isLoadingLinkedChats &&
                          isLoadingFolders &&
                          "Loading locations and linked chats..."}
                        {isLoadingLinkedChats &&
                          !isLoadingFolders &&
                          "Loading linked chats..."}
                        {!isLoadingLinkedChats &&
                          isLoadingFolders &&
                          "Loading locations..."}
                      </p>
                    </div>
                  </div>
                )}

                {/* Close button - disabled while loading */}
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4 z-50">
                  <button
                    type="button"
                    className={`bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0 ${
                      isModalLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={() => !isModalLoading && dispatch(closeModal())}
                    disabled={isModalLoading}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                <div
                  className={`pr-8 w-full ${isModalLoading ? "filter blur-sm pointer-events-none" : ""}`}
                >
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h1"
                      className="text-lg font-medium text-gray-900"
                    ></Dialog.Title>

                    <Formik
                      initialValues={{
                        location: currentLocation || "",
                        tag: processedTag || "0",
                        linkedChats: [],
                      }}
                      enableReinitialize="true"
                      validate={(values) => {
                        const errors = {};
                        if (!values.location) {
                          errors.location = "Please select a location";
                        }
                        return errors;
                      }}
                      onSubmit={handleSubmit}
                    >
                      {({
                        values,
                        touched,
                        errors,
                        setFieldValue,
                        handleSubmit,
                        isSubmitting,
                        isValid,
                        dirty,
                      }) => (
                        <Form
                          onSubmit={handleSubmit}
                          className="space-y-8 m-4 w-full"
                        >
                          <h2 className="text-lg font-medium text-gray-900 mb-4">
                            Move Booking
                          </h2>

                          {/* Property Selector - Only for QL */}
                          <div className="space-y-4">
                            {process.env.REACT_APP_COLOUR === "QL" && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-700">
                                Property:
                              </h3>
                              <div className="flex items-center gap-2">
                                <select
                                  value={selectedPropertyId}
                                  onChange={(e) => {
                                    const newPropId = e.target.value;
                                    setSelectedPropertyId(newPropId);

                                    // If current location is an under_property folder,
                                    // check if it's still valid for the new property
                                    const newFiltered =
                                      getFilteredUnderPropertyFolders(newPropId);
                                    const currentIsUnderProp =
                                      allFoldersRaw.some(
                                        (f) =>
                                          f.under_property &&
                                          (f.name === values.location ||
                                            (f.name === "All Residents" &&
                                              values.location === "Residents")),
                                      );

                                    if (currentIsUnderProp) {
                                      const stillValid = newFiltered.some(
                                        (f) => f.name === values.location,
                                      );
                                      if (!stillValid) {
                                        setFieldValue("location", "");
                                        setFieldValue("tag", "0");
                                      }
                                    }
                                  }}
                                  className="capitalize cursor-pointer relative p-2 bg-light_background rounded-md focus:outline-none transition duration-150 ease-in-ease-out hover:-translate-y-0.5 flex-1"
                                >
                                  <option value="">No Property</option>
                                  {properties.map((property) => (
                                    <option
                                      key={property.id}
                                      value={property.id}
                                      className={classNames(
                                        propertyMatchesGroup(
                                          current_booking?.property_id,
                                          property,
                                        )
                                          ? "text-white bg-primary"
                                          : "text-black",
                                      )}
                                    >
                                      {property.name}
                                    </option>
                                  ))}
                                </select>
                                {hasPropertyChanged && (
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded whitespace-nowrap">
                                    Changed
                                  </span>
                                )}
                              </div>
                              {hasPropertyChanged && current_booking?.unit_id > 0 && (
                                <p className="text-xs text-red-600 font-medium mt-1 ml-1">
                                  Please note: the current unit assignment will be lost if the property changes
                                </p>
                              )}
                              {hasPropertyChanged && current_booking?.bed_id > 0 && (
                                <p className="text-xs text-red-600 font-medium mt-1 ml-1">
                                  Please note: the current bed assignment will be lost if the property changes
                                </p>
                              )}
                              {current_booking?.property_id && (
                                <p className="text-xs text-gray-400 mt-1 ml-1">
                                  Current: {currentPropertyName}
                                </p>
                              )}
                              {!current_booking?.property_id && (
                                <p className="text-xs text-orange-500 mt-1 ml-1">
                                  No property assigned - select one to see
                                  property folders
                                </p>
                              )}
                            </div>
                            )}

                            {/* Location Selector */}
                            <div>
                              <h3 className="text-sm font-medium text-gray-700">
                                Location:
                              </h3>
                              <div>
                                <Field
                                  as="select"
                                  name="location"
                                  className="capitalize cursor-pointer relative p-2 mx-2 bg-light_background rounded-md focus:outline-none transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                                  onChange={(e) => {
                                    const newLocation = e.target.value;
                                    setFieldValue("location", newLocation);
                                    setAckBackward(false);

                                    if (newLocation) {
                                      const currentTagValue = values.tag;
                                      const tagStatus = getTagDisplayStatus(
                                        currentTagValue === "orphaned"
                                          ? current_booking?.tag
                                          : currentTagValue,
                                        newLocation,
                                      );

                                      if (tagStatus === "orphaned") {
                                        setFieldValue("tag", "orphaned");
                                      } else if (tagStatus === "notag") {
                                        setFieldValue("tag", "0");
                                      }
                                    }
                                  }}
                                >
                                  <option value="">Select a location</option>

                                  {/* Display locations with no tags (sorted) */}
                                  {locations.noTags.length > 0 && (
                                    <optgroup label="Folders">
                                      {locations.noTags.map((location) => (
                                        <option
                                          key={location.id}
                                          value={location.name}
                                          className={classNames(
                                            location.name ===
                                              current_booking?.location
                                              ? "text-white bg-primary"
                                              : "text-black",
                                          )}
                                        >
                                          {location.display_name || location.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}

                                  {/* Display property-specific folders (dynamic) */}
                                  {filteredUnderPropertyFolders.length > 0 && (
                                    <optgroup
                                      label={
                                        selectedPropertyId
                                          ? `${properties.find((p) => String(p.id) === String(selectedPropertyId))?.name || "Property"} Folders`
                                          : "Property Folders"
                                      }
                                    >
                                      {filteredUnderPropertyFolders.map(
                                        (location) => (
                                          <option
                                            key={location.id}
                                            value={location.name}
                                            className={classNames(
                                              location.name ===
                                                current_booking?.location
                                                ? "text-white bg-primary"
                                                : "text-black",
                                            )}
                                          >
                                            {location.display_name || location.name}
                                          </option>
                                        ),
                                      )}
                                    </optgroup>
                                  )}

                                  {/* Display locations with tags (sorted) */}
                                  {locations.hasTags.length > 0 && (
                                    <optgroup label="Folders With Tags">
                                      {locations.hasTags.map((location) => (
                                        <option
                                          key={location.id}
                                          value={location.name}
                                          className={classNames(
                                            location.name ===
                                              current_booking?.location
                                              ? "text-white bg-primary"
                                              : "text-black",
                                          )}
                                        >
                                          {location.display_name || location.name}
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                </Field>

                                {errors.location && touched.location && (
                                  <p className="text-red-400 text-sm ml-2">
                                    {errors.location}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Show tag selector for archived bookings or when location has tags */}
                          {(current_booking?.archived ||
                            (values.location &&
                              locations.hasTags.some(
                                (loc) => loc.name === values.location,
                              ))) && (
                            <div className="mt-4">
                              <h3 className="text-sm font-medium mb-2">Tag:</h3>
                              <Field
                                as="select"
                                name="tag"
                                className="capitalize cursor-pointer relative p-2 mx-2 bg-light_background rounded-md transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                              >
                                <option value="0">No Tag</option>

                                {values.tag === "orphaned" && (
                                  <option value="orphaned">Orphaned</option>
                                )}

                                {getTagsForLocation(values.location).map(
                                  (tag) => (
                                    <option
                                      key={tag.id}
                                      value={tag.id}
                                      className={classNames(
                                        tag.id === current_booking?.tag
                                          ? "text-white bg-primary"
                                          : "text-black",
                                      )}
                                    >
                                      {tag.name}
                                    </option>
                                  ),
                                )}
                              </Field>
                            </div>
                          )}

                          {/* Linked Chats Section - Only show for non-archived bookings */}
                          {!current_booking?.archived && (
                            <>
                              {isLoadingLinkedChats ? (
                                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                                  <p className="text-sm text-gray-500">
                                    Loading linked chats...
                                  </p>
                                </div>
                              ) : displayableLinkedChatsCount > 0 ? (
                                <div className="mt-6">
                                  <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-sm font-medium text-gray-700">
                                      Move Linked Chats
                                    </h3>
                                  </div>
                                  <div className="space-y-2 max-h-40 overflow-y-auto p-2 border rounded-md">
                                    {linkedChats
                                      .filter((chat) => {
                                        const displayBooking =
                                          chat.booking?.id === props.id
                                            ? chat.linked_booking
                                            : chat.booking;
                                        const linkType =
                                          chat.booking?.id === props.id
                                            ? chat.linked_type
                                            : chat.type;
                                        const excludedTenantTypes = [
                                          "FN",
                                          "PA",
                                          "SP",
                                        ];
                                        const excludedLinkTypes = [
                                          "Funder",
                                          "Parent",
                                          "Sponsor",
                                        ];
                                        return (
                                          !excludedTenantTypes.includes(
                                            displayBooking?.tenant_type,
                                          ) &&
                                          !excludedLinkTypes.includes(linkType)
                                        );
                                      })
                                      .map((chat) => {
                                        const displayBooking =
                                          chat.booking?.id === props.id
                                            ? chat.linked_booking
                                            : chat.booking;
                                        const displayUser =
                                          chat.booking?.id === props.id
                                            ? chat.linked_user
                                            : chat.user;
                                        const chatId =
                                          chat.booking?.id === props.id
                                            ? chat.linked_booking_id
                                            : chat.booking?.id;

                                        return (
                                          <div
                                            key={chat.id}
                                            className="flex items-center"
                                          >
                                            <input
                                              id={`chat-${chat.id}`}
                                              name="linkedChats"
                                              type="checkbox"
                                              checked={selectedLinkedChats.some(
                                                (id) =>
                                                  String(id) === String(chatId),
                                              )}
                                              onChange={() =>
                                                handleLinkedChatToggle(chatId)
                                              }
                                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label
                                              htmlFor={`chat-${chat.id}`}
                                              className="ml-2 block text-sm text-gray-700"
                                            >
                                              {`${displayUser?.name || ""} ${
                                                displayUser?.surname || ""
                                              } - ${
                                                displayBooking?.booking_reference ||
                                                ""
                                              } `}
                                              {displayBooking?.tenant_type && (
                                                <span className="text-xs bg-primary text-white px-2 rounded">
                                                  {displayBooking.tenant_type}
                                                </span>
                                              )}
                                            </label>
                                          </div>
                                        );
                                      })}
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">
                                    Selected {selectedLinkedChats.length} of{" "}
                                    {displayableLinkedChatsCount} linked chats
                                  </p>
                                </div>
                              ) : (
                                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                                  <p className="text-sm text-gray-500">
                                    No linked chats found.
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                          {isBackwardMove(values.location) && (
                            <div className="mt-6 rounded-md border border-red-300 bg-red-50 p-3">
                              <p className="text-sm font-semibold text-red-700">
                                This is back in the process
                              </p>
                              <p className="mt-1 text-sm text-red-600">
                                You are moving this chat from Residents back to
                                "{values.location}", an earlier stage of the
                                process. Are you sure?
                              </p>
                              <label className="mt-2 flex items-center text-sm text-red-700">
                                <input
                                  type="checkbox"
                                  checked={ackBackward}
                                  onChange={(e) =>
                                    setAckBackward(e.target.checked)
                                  }
                                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-red-300 rounded mr-2"
                                />
                                Yes, move it back to "{values.location}"
                              </label>
                            </div>
                          )}

                          <div className="mt-6">
                            <SubmitButton
                              name="Save"
                              disabled={
                                !isValid ||
                                isSubmitting ||
                                (!dirty && !hasPropertyChanged) ||
                                (isBackwardMove(values.location) && !ackBackward)
                              }
                              isSubmitting={isSubmitting}
                              text="Assign"
                            />
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
